import {
  BookOpenIcon,
  CalendarDaysIcon,
  CalendarRangeIcon,
  ClipboardCheckIcon,
  CookieIcon,
  DownloadIcon,
  GiftIcon,
  InfinityIcon,
  LifeBuoyIcon,
  MailIcon,
  MoonIcon,
  SandwichIcon,
  ShieldCheckIcon,
  ShoppingCartIcon,
  SmartphoneIcon,
  SnowflakeIcon,
  TablePropertiesIcon,
  ZapIcon,
  type LucideIcon,
} from 'lucide-react';

export const PRICE = 'AR$ 16.990';

/** Pegá aquí las URLs de las imágenes reales. Si quedan vacías se muestra el espacio reservado. */
export const assets = {
  mainMockup: 'https://cdn.magicpatterns.com/uploads/pXhdofZSGmJseuJEK18VUt/Mockup_colorido_de_Viandas_Express.png',
};

export type IconItem = { icon: LucideIcon; title: string; text?: string };

export const heroChips: IconItem[] = [
  { icon: DownloadIcon, title: 'Digital' },
  { icon: ZapIcon, title: 'Acceso inmediato' },
  { icon: SmartphoneIcon, title: 'Celular, tablet o compu' },
  { icon: GiftIcon, title: '+ 5 bonos' },
];

export const painItems = [
  'La misma pregunta todas las noches, justo cuando querés terminar el día.',
  'Repetir siempre lo mismo porque no hay tiempo para pensar otra cosa.',
  'Comprar sin plan: falta lo que necesitabas y sobra lo que no usaste.',
  'Mañanas a las corridas porque la noche anterior no quedó nada listo.',
  'Freezer lleno, pero sin saber qué hay ni qué conviene usar primero.',
  'Un imprevisto que desarma toda la semana.',
];

export const weekTabs = ['Semana 1', 'Semana 2', 'Semana 3', 'Semana 4', 'Plan B'];

export const modules: IconItem[] = [
  { icon: CalendarDaysIcon, title: 'Calendario de 30 días', text: 'Sabés qué preparar cada día sin decidir desde cero.' },
  { icon: SandwichIcon, title: 'Recetas y soluciones paso a paso', text: 'Preparaciones explicadas para incorporar a la rutina.' },
  { icon: ShoppingCartIcon, title: 'Compras organizadas', text: 'Qué comprar antes de empezar la semana, sin olvidos.' },
  { icon: MoonIcon, title: 'Preparación anticipada', text: 'Qué podés adelantar para sacarte trabajo de encima.' },
  { icon: LifeBuoyIcon, title: 'Plan B para imprevistos', text: 'Alternativas para cuando el día no sale como esperabas.' },
  { icon: SnowflakeIcon, title: 'Conservación y transporte', text: 'Orientación práctica para guardar y llevar las preparaciones.' },
];

export const systemIncludes = [
  'Qué preparar cada día',
  'Qué comprar',
  'Qué adelantar',
  'Qué va al freezer',
  'Qué hacer si el plan cambia',
  'Acceso desde cualquier dispositivo',
];

export type Bonus = {
  number: number;
  image: string;
  title: string;
  micro: string;
  icon: LucideIcon;
};

export const bonuses: Bonus[] = [
  { number: 1, image: 'https://cdn.magicpatterns.com/uploads/jbTMVwMEXuicTqYfZrGMBP/mockup-bono-1.webp', title: '20 Meriendas Rápidas', micro: 'Para cuando el problema no termina en la vianda.', icon: CookieIcon },
  {
    number: 2,
    image: 'https://cdn.magicpatterns.com/uploads/nEbFbL3kHznk6qEvcj9jn5/mockup-bono-2.webp',
    title: 'Guía de Compras Semanales por Sector del Súper',
    micro: 'Para entrar al súper sabiendo qué buscar.',
    icon: ShoppingCartIcon,
  },
  {
    number: 3,
    image: 'https://cdn.magicpatterns.com/uploads/tvfbCoqnERo929hXS6mihX/mockup-bono-3.webp',
    title: 'Checklist de Organización del Domingo',
    micro: 'Para empezar la semana con ventaja.',
    icon: ClipboardCheckIcon,
  },
  {
    number: 4,
    image: 'https://cdn.magicpatterns.com/uploads/bA4LcwtSoewxmNdH9YSWmK/mockup-bono-4.webp',
    title: 'Planificador Mensual Imprimible',
    micro: 'Para ver el mes completo de un vistazo.',
    icon: CalendarRangeIcon,
  },
  {
    number: 5,
    image: 'https://cdn.magicpatterns.com/uploads/qxaswgn2iWEfvAbuCS7x8X/mockup-bono-5_.webp',
    title: 'Tabla de Stock de Freezer',
    micro: 'Para saber qué tenés antes de volver a cocinar.',
    icon: TablePropertiesIcon,
  },
];

export const offerItems = [
  '30 días de planificación',
  'Recetas y soluciones paso a paso',
  'Compras organizadas',
  'Preparación anticipada',
  'Plan B para imprevistos',
  'Conservación y transporte',
];

export const comparisonRows = [
  { situation: 'Hoy tenés poco tiempo', without: 'Improvisás', with: 'Opción rápida del Plan B' },
  { situation: 'Te olvidaste de preparar', without: 'Corrida a la mañana', with: 'Alternativa lista para resolver' },
  { situation: 'No descongelaste', without: 'Cambiás todo', with: 'Reemplazo sugerido' },
  { situation: 'Falta un ingrediente', without: 'Salís a comprar', with: 'Variante con lo que tenés' },
  { situation: 'Cambió el día', without: 'Se desarma la semana', with: 'Reacomodás sin empezar de cero' },
];

/** Supuesto editable del cálculo de tiempo. Se muestra como ejemplo, no como estadística. */
export const timeCost = { minutesPerNight: 20, daysPerWeek: 5, weeksPerYear: 40 };

export const costRows = [
  { without: 'Decidir cada noche', cost: 'Una pregunta más al final de un día largo' },
  { without: 'Comprar sin lista', cost: 'Ingredientes que faltan y otros que se pierden' },
  { without: 'Freezer sin registro', cost: 'Comida olvidada al fondo' },
  { without: 'Sin plan para imprevistos', cost: 'Un día complicado desarma la semana' },
  { without: 'Repetir siempre lo mismo', cost: 'La misma vianda por falta de tiempo, no por elección' },
];

export const faqs = [
  {
    q: '¿Cómo recibo el producto?',
    a: 'Es un producto 100% digital. Una vez confirmado el pago, recibís las instrucciones para acceder al material.',
  },
  {
    q: '¿Necesito imprimirlo?',
    a: 'No. Podés consultar el contenido desde celular, tablet o computadora. Los materiales específicamente diseñados como imprimibles pueden imprimirse de forma opcional.',
  },
  {
    q: '¿Necesito saber cocinar?',
    a: 'No necesitás técnicas avanzadas. Las preparaciones incluyen instrucciones para que puedas seguirlas paso a paso.',
  },
  {
    q: '¿Tengo que seguir los 30 días exactamente en ese orden?',
    a: 'No. La planificación funciona como una estructura que podés adaptar según tu rutina, disponibilidad de ingredientes y preferencias familiares.',
  },
  {
    q: '¿También incluye meriendas?',
    a: 'Sí. La compra incluye como bono una guía independiente con 20 opciones de merienda.',
  },
  {
    q: '¿Puedo aprovechar el freezer?',
    a: 'Sí. El material incluye herramientas relacionadas con preparación anticipada y freezer, además de una Tabla de Stock de Freezer incluida como bono.',
  },
  {
    q: '¿Incluye información sobre conservación y transporte?',
    a: 'Sí. El producto contiene orientación general para organizar la conservación y el transporte de las preparaciones.',
  },
  {
    q: '¿Qué ocurre si mi hijo/a tiene alergias o necesidades alimentarias específicas?',
    a: 'El material ofrece información general y no constituye asesoramiento médico o nutricional personalizado. Ante alergias, intolerancias, patologías o necesidades específicas, adaptá las preparaciones según las indicaciones del profesional correspondiente.',
  },
  {
    q: '¿Cuánto tiempo tengo acceso?',
    a: 'El acceso al material adquirido es de por vida.',
  },
  {
    q: '¿Qué pasa si compro y el producto no es para mí?',
    a: 'La compra cuenta con una garantía de 7 días, sujeta a las condiciones informadas en la página.',
  },
];

export const finalSummary: IconItem[] = [
  { icon: BookOpenIcon, title: '30 Días de Viandas Resueltas' },
  { icon: GiftIcon, title: '+ 5 bonos digitales' },
  { icon: MailIcon, title: 'Acceso inmediato' },
  { icon: InfinityIcon, title: 'Acceso de por vida' },
  { icon: ShieldCheckIcon, title: 'Garantía de 7 días' },
];
